"use strict"

import React, { useEffect, useState } from "react"
import { Tab, Tabs, Button } from "react-bootstrap"
import moment from "moment"
import { csvFormatRows } from "d3-dsv"
import { FormattedMessage } from "react-intl"

import { PageTitle } from "../../config/AppSettingsConfigProvider"
import Procurement from "./procurement/Procurement"
import InventoryItemsTable from "./inventory_items/InventoryItemsTable"
import ManageInventory from "./manage_inventory/ManageInventory"
import DataExportButton from "../../shared_components/graphing/modals/DataExportButton"
import { withInventoryConfig } from "../../config/InventoryConfigProvider"
import PageLoadingPanel from "../../shared_components/widgets/PageLoadingPanel"

const InventoryDash = ({ intl, user_permissions = [], getInventoryItems }) => {
  const hasPermission = (permission_code) => {
    return !!user_permissions.includes(permission_code)
  }

  // States
  const [activeTab, setActiveTab] = useState("procurement")
  const [available_items, setAvailableItems] = useState([])
  const [exportDataThunk, setExportDataThunk] = useState([])

  // Effects
  useEffect(() => {
    if (available_items.length > 0) {
      setExportDataThunk(generateCSVRows(available_items))
    }
  }, [available_items])

  const onProcurementItemReceived = () => {
    // Refresh Inventory Items Table
    getInventoryItems()
    setActiveTab("available_inventory")
  }

  const generateCSVRows = (items = []) => {
    let data_export = [
      ["Name", "Description", "Type", "Lot #", "UoM", "Quantity", "Retest Date", "Last Received Date"],
    ]
    items.forEach((item) => {
      data_export.push([
        item["sku"]["name"],
        item["sku"]["description"],
        item["sku"]["inventory_type"]["name"],
        item["lot_no"],
        item["uom"],
        item["available_quantity"],
        moment(item["retest_date"]).format(),
        moment(item["last_received_date"]).format(),
      ])
    })

    return csvFormatRows(data_export)
  }

  return (
    <div className={"full-height"}>
      <div className={"content-block full-height-min"}>
        <PageTitle glyph={"inbox"} id={"navigation.inventory.management"} description={"Inventory"} />
        {user_permissions.length === 0 ? (
          <PageLoadingPanel />
        ) : (
          hasPermission("view_inventory_management") && (
            <div>
              <Tabs
                defaultActiveKey={"procurement"}
                activeKey={activeTab}
                onSelect={(k) => setActiveTab(k)}
                id={"inventory-management-tabs"}
                className={"full-height"}
              >
                <Tab
                  key={"procurement"}
                  eventKey={"procurement"}
                  title={intl.formatMessage({ id: "inventory.procurement" })}
                >
                  <div className={"tab-body my_tab_body"}>
                    <Procurement onItemReceived={onProcurementItemReceived} />
                  </div>
                </Tab>
                <Tab
                  key={"available_inventory"}
                  eventKey={"available_inventory"}
                  title={intl.formatMessage({ id: "stock.available" })}
                >
                  <div className={"tab-body my_tab_body"}>
                    <div className={"row"}>
                      <div className={"col-8"}>
                        <h4 style={{ margin: "5px 0px" }}>
                          <span className={"fa fa-inbox"} />
                          &nbsp;&nbsp;
                          <FormattedMessage id={"inventory.available_items"} />
                        </h4>
                      </div>
                      <div className={"col-4"} align={"right"}>
                        <DataExportButton
                          intl={intl}
                          filename={"Available_Inventory.csv"}
                          disabled={available_items.length === 0}
                          dataThunk={() => exportDataThunk}
                        />
                        &nbsp;&nbsp;
                        <Button variant={"outline-primary"} onClick={() => getInventoryItems()}>
                          <span className={"fa fa-refresh"} />
                          &nbsp;&nbsp;
                          <FormattedMessage id={"general.refresh"} />
                        </Button>
                      </div>
                    </div>
                    <hr style={{ margin: "15px 0px 25px 0px" }} />
                    <InventoryItemsTable setAvailableItems={setAvailableItems} />
                  </div>
                </Tab>
                {hasPermission("can_manage_inventory") && (
                  <Tab
                    key={"manage_inventory"}
                    eventKey={"manage_inventory"}
                    title={intl.formatMessage({ id: "inventory.manage-inventory" })}
                  >
                    <div className={"tab-body my_tab_body"}>
                      <ManageInventory intl={intl} />
                    </div>
                  </Tab>
                )}
              </Tabs>
            </div>
          )
        )}
      </div>
    </div>
  )
}

export default withInventoryConfig(InventoryDash)
